import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  directoryApi,
  type CreateSiteRequest,
  type UpdateSiteRequest,
} from '../client';
import { siteWithMembersKeys, userSiteKeys } from './useUserSites';

/** Stable query-key factory for site-scoped caches. */
export const siteKeys = {
  all: ['directory', 'site'] as const,
  byOrganisation: (organisationId: string) =>
    ['directory', 'site', 'org', organisationId] as const,
};

/**
 * Mutation wrapper around POST `/sites`. Used by the Organisation
 * settings page to seed a `Main Office` site whenever it creates the
 * first address for an org, and by the Sites tab in Users & Access for
 * ad-hoc site creation. Invalidates the org-scoped site list AND the
 * sites-with-members cache (Sites tab read source).
 */
export function useCreateSite() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateSiteRequest>({
    mutationKey: [...siteKeys.all, 'create'],
    mutationFn: (body) => directoryApi.createSite(body),
    onSuccess: (_id, vars) => {
      qc.invalidateQueries({ queryKey: siteKeys.byOrganisation(vars.organisationId) });
      qc.invalidateQueries({
        queryKey: siteWithMembersKeys.byOrg(vars.organisationId),
      });
    },
  });
}

/**
 * Mutation wrapper around PUT `/sites`. SiteId travels in the body
 * (matches the BE Mediator command shape). Caller must include the
 * organisationId in the variables so we can target the right cache —
 * the BE response only returns the (unchanged) id, not the org.
 */
export function useUpdateSite() {
  const qc = useQueryClient();
  return useMutation<
    string,
    Error,
    { organisationId: string; body: UpdateSiteRequest }
  >({
    mutationKey: [...siteKeys.all, 'update'],
    mutationFn: ({ body }) => directoryApi.updateSite(body),
    onSuccess: (_id, { organisationId }) => {
      qc.invalidateQueries({ queryKey: siteKeys.byOrganisation(organisationId) });
      qc.invalidateQueries({
        queryKey: siteWithMembersKeys.byOrg(organisationId),
      });
    },
  });
}

/**
 * Mutation wrapper around DELETE `/sites/{id}`. The BE soft-deletes
 * (Site is `ISoftDelete`), so the row is recoverable but no longer
 * surfaces in queries. Caller must pass `organisationId` in the
 * variables so we can target the right cache — the response is empty.
 *
 * Also nukes the per-user `userSiteKeys` namespace because removing a
 * site implicitly drops it from any users that had it assigned, and
 * those caches are keyed per-user (no cheap way to know which users
 * were affected without diffing).
 */
export function useDeleteSite() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; organisationId: string }>({
    mutationKey: [...siteKeys.all, 'delete'],
    mutationFn: ({ id }) => directoryApi.deleteSite(id),
    onSuccess: (_res, { organisationId }) => {
      qc.invalidateQueries({ queryKey: siteKeys.byOrganisation(organisationId) });
      qc.invalidateQueries({
        queryKey: siteWithMembersKeys.byOrg(organisationId),
      });
      qc.invalidateQueries({ queryKey: userSiteKeys.all });
    },
  });
}
